import React from 'react';
import { styled } from '@withjoy/joykit';

// Inline internal link used within FAQ answers. Inherits the surrounding text
// size so it flows inline; underlined so it reads as a link.
const FaqLink = styled.a`
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    text-decoration: none;
  }
`;

export type FaqAnswerLink = Readonly<{
  /** Exact substring within the answer to turn into a link (first match). */
  phrase: string;
  /** Root-relative destination (e.g. `/wedding-invitations`). */
  href: string;
}>;

/**
 * Turns specific phrases within a plain-text FAQ answer into inline internal
 * links, returning a React node for display. The original string is kept
 * untouched for the FAQ JSON-LD schema (`acceptedAnswer.text`) — only the
 * rendered copy gains anchors. Adds internal links to break the save-the-date
 * page out of structural isolation (GROW-1788).
 */
export const linkifyAnswer = (answer: string, links: ReadonlyArray<FaqAnswerLink>): React.ReactNode => {
  let segments: React.ReactNode[] = [answer];

  links.forEach(({ phrase, href }, linkIndex) => {
    segments = segments.flatMap((segment, segmentIndex) => {
      if (typeof segment !== 'string') {
        return [segment];
      }
      const matchIndex = segment.indexOf(phrase);
      if (matchIndex === -1) {
        return [segment];
      }
      return [
        segment.slice(0, matchIndex),
        <FaqLink
          key={`faq-link-${linkIndex}-${segmentIndex}`}
          href={href}
        >
          {phrase}
        </FaqLink>,
        segment.slice(matchIndex + phrase.length),
      ];
    });
  });

  return segments;
};
